import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ContentManagerService } from '../../services/content-manager.service';
import { EditorService } from '../../services/editor.service';
import { CustomModalService } from '../../shared/as-components/custom-modal/modal.service';
import { HTMLElementModel, UploadPhoto } from '../models/content.model';
import { InlineImageEditorAltComponent } from './inline-image-editor-alt/inline-image-editor-alt.component';
import { InlineImageEditorLinkComponent } from './inline-image-editor-link/inline-image-editor-link.component';

@Component({
  selector: 'app-inline-image-editor',
  templateUrl: './inline-image-editor.component.html',
  styleUrl: './inline-image-editor.component.scss',
  standalone: false,
})
export class InlineImageEditorComponent implements OnInit, AfterViewInit {
  @Input() value!: HTMLElement;
  @Output() save = new EventEmitter<HTMLElementModel>();

  photo = {
    u_id: '',
    media_id: '',
    url: '',
    alt: '',
  } as UploadPhoto;

  oldImageName: string | null = null;
  uploadProgress = 0;
  @Output() cancel = new EventEmitter<void>();
  @Output() photoOutput = new EventEmitter();
  private savedRange: Range | null = null;
  @ViewChild('toolbar') toolbarRef!: ElementRef<HTMLDivElement>;
  @ViewChild('editable') editableRef!: ElementRef<HTMLDivElement>;

  innerHTML: string;
  innerHtmlSafe: SafeHtml;
  anchor: string;

  public dragging = false;

  // mediaUrl = '';
  // mediaId = '';

  constructor(
    private hostRef: ElementRef<HTMLElement>,
    private contentManagerService: ContentManagerService,
    private editorService: EditorService,
    private modal: CustomModalService,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer,
  ) {
  }

  ngOnInit(): void {
    this.oldImageName = this.getFileNameFromHtml(this.value);
  }

  private getFileNameFromHtml(html: any): string | null {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const img = doc.querySelector<HTMLImageElement>('img[src]');
    if (!img) {
      return null;
    }
    const src = img.getAttribute('src')!;
    const url = new URL(src, window.location.origin);
    return url.pathname.split('/').pop() ?? null;
  }

  ngAfterViewInit(): void {
    this.value.style.display = '';
    // this.editableRef.nativeElement.appendChild(this.value);
    this.positionToolbar();
    this.updateInnerHtml(this.value?.innerHTML || '');
    this.cdr.detectChanges();
    setTimeout(() => {
      const el = this.editableRef.nativeElement;
      el.focus();
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
    });

    // this.editableRef.nativeElement.appendChild(this.value);

    const el = this.value as HTMLElement;
    let parent = el.parentElement;
    while (parent) {
      if (parent.classList) {
        for (const className of Array.from(parent.classList)) {
          if (className.startsWith('block_')) {
            const imgEl = el.querySelector('img');
            if (imgEl) {
              this.photo.alt = imgEl.getAttribute('alt') ?? '';
            }
            this.photo.u_id = className;
            break;
          }
        }
        if (this.photo.u_id) {
          break;
        }
      }
      parent = parent.parentElement;
    }
  }

  /** Pozycjonuje toolbar tuż nad edytorem */
  private positionToolbar() {
    const tb = this.toolbarRef.nativeElement;
    const editor = this.editableRef.nativeElement;
    const host = this.hostRef.nativeElement;
    const editorRect = editor.getBoundingClientRect();
    const hostRect = host.getBoundingClientRect();
    const margin = 8;
    const top = editorRect.top - hostRect.top - tb.offsetHeight - margin;

    tb.style.position = 'absolute';
    tb.style.top = top - 40 + 'px';
    tb.style.zIndex = '100';
  }

  replaceImage(files: FileList): void {
    const file = files.item(0);
    if (!file) {
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const img = this.getCurrentImgElement(this.value);
      if (img) {
        img.src = reader.result as string;
      }

      const doc = new DOMParser().parseFromString(this.innerHTML, 'text/html');
      const imgInHtml = doc.querySelector<HTMLImageElement>('img');
      if (imgInHtml) {
        imgInHtml.src = reader.result as string;
      }
      this.updateInnerHtml(doc.body.innerHTML);
    };
    reader.readAsDataURL(file);

    this.contentManagerService.uploadMedia(file, {
        updateMediaIds: true,
        onSuccess: (response) => {
          this.photo.url = response.full_url;
          this.photo.media_id = response.id;
          this.cdr.detectChanges();
        },
      },
      (progress) => (this.uploadProgress = progress),
    ).subscribe({
      error: (err) => console.log(err),
    });
  }

  private getCurrentImgElement(value: HTMLElement): HTMLImageElement | null {
    const root = value;
    if (!root) {
      return null;
    }

    return root.tagName === 'IMG'
      ? (root as HTMLImageElement)
      : root.querySelector<HTMLImageElement>('img');
  }

  onEditAlt(): void {
    const img = this.getImgFromEditor();
    if (!img) {
      return;
    }

    // Pobierz aktualny alt
    const currentAlt = img.alt || '';

    const dialogRef = this.modal.open(InlineImageEditorAltComponent, {
      width: '560px',
      data: {data: currentAlt},
      disableClose: false,
    });

    dialogRef.afterClosed().subscribe((alt?: string) => {
      if (!alt) {
        return;
      }
      img.alt = alt;
      this.photo.alt = alt;
      this.updateInnerHtml(this.editableRef.nativeElement.innerHTML);
      this.cdr.detectChanges();
    });
  }

  getImgFromEditor(): HTMLImageElement | null {
    // img może być bezpośrednio lub w a > img
    return this.editableRef.nativeElement.querySelector('img');
  }

  onSave() {
    const firstChildElement = this.editableRef.nativeElement
      .firstChild as HTMLElement;
    const params = {
      data: firstChildElement.outerHTML || this.innerHTML,
      anchor: this.anchor,
    };
    this.save.emit(params);

    this.onCancel();
  }

  onCancel() {
    this.cancel.emit();
    this.photoOutput.emit(this.photo);
    console.log('działa zamykanie image');
    console.log(this.photo);
  }

  @HostListener('keydown', ['$event'])
  preventTyping(ev: KeyboardEvent) {
    const allowed = [
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Delete',
      'Escape',
      'Tab',
    ];
    if (!allowed.includes(ev.key)) {
      ev.preventDefault();
    }
  }

  @HostListener('paste', ['$event'])
  preventPaste(ev: ClipboardEvent) {
    ev.preventDefault();
  }

  @HostListener('document:selectionchange')
  onSelectionChange() {
    if (!this.dragging) {
      return;
    }
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) {
      return;
    }

    const range = sel.getRangeAt(0).getBoundingClientRect();
    const tb = this.toolbarRef.nativeElement;
    tb.style.left = range.x + 'px';
    tb.style.top = window.scrollY + range.y - tb.offsetHeight - 8 + 'px';
  }

  @HostListener('document:mousedown', ['$event'])
  closeWhenOutside(ev: MouseEvent): void {
    const target = ev.target as HTMLElement;
    const inEditor = this.editableRef.nativeElement.contains(target) ?? false;
    const inToolbar = this.toolbarRef.nativeElement.contains(target) ?? false;
    const inModal = !!target.closest('.custom-modal');

    if (!inEditor && !inToolbar && !inModal) {
      this.onSave();
    }
  }

  @HostListener('document:selectionchange')
  captureSelection() {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) {
      return;
    }

    const editor = this.editableRef.nativeElement;
    if (editor.contains(sel.anchorNode)) {
      this.savedRange = sel.getRangeAt(0).cloneRange();
    }
  }

  applyCommand() {
    const originalEl: HTMLElement = this.hostRef.nativeElement;
    let existingAnchor = this.findFirstParentAnchor(originalEl);
    if (!existingAnchor) {
      existingAnchor = this.getFirstAnchor(originalEl);
    }

    const dialogRef = this.modal.open(InlineImageEditorLinkComponent, {
      width: '560px',
      data: {data: this.value, anchor: existingAnchor},
      disableClose: false,
    });

    dialogRef.afterClosed().subscribe((href?: string) => {
      if (!href) {
        return;
      }
      this.anchor = href;
      this.photo.url = this.anchor;
      if (existingAnchor) {
        existingAnchor.href = href;
      } else {
        const anchorElement = document.createElement('a');
        anchorElement.href = href;
        anchorElement.target = '_blank';
        anchorElement.rel = 'noopener noreferrer';
        originalEl.parentNode?.insertBefore(anchorElement, originalEl);
        anchorElement.appendChild(originalEl);
      }

      this.updateInnerHtml(this.editableRef.nativeElement.innerHTML);
      this.cdr.detectChanges();
    });
  }

  findFirstParentAnchor(originalEl: HTMLElement): HTMLAnchorElement | null {
    let current: HTMLElement | null = originalEl;
    while (current && current !== document.documentElement) {
      if (current.tagName === 'A') {
        return current as HTMLAnchorElement;
      }
      current = current.parentElement;
    }
    return null;
  }

  getFirstAnchor(el: HTMLElement): HTMLAnchorElement | null {
    return el.tagName === 'A'
      ? (el as HTMLAnchorElement)
      : el.querySelector<HTMLAnchorElement>('a');
  }

  onUnlink(): void {
    const img = this.editableRef.nativeElement.querySelector('img');
    if (img) {
      this.removeImageLink(img);
      this.updateInnerHtml(this.editableRef.nativeElement.innerHTML);
      this.cdr.detectChanges();
    }
  }

  removeImageLink(img: HTMLElement): void {
    const anchor = this.findFirstParentAnchor(img);
    if (anchor && anchor.parentNode) {
      while (anchor.firstChild) {
        anchor.parentNode.insertBefore(anchor.firstChild, anchor);
      }
      anchor.parentNode.removeChild(anchor);
    }
  }

  private updateInnerHtml(html: string): void {
    this.innerHTML = html;
    this.innerHtmlSafe = this.sanitizer.bypassSecurityTrustHtml(this.innerHTML);
  }
}
